import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Plus, Trash2, Copy, CheckCircle, AlertCircle, Wallet, Coins, X } from 'lucide-react';

function Whitelist({ tokenId, tokenData }) {
console.log('🔍 Whitelist Debug - tokenId:', tokenId, 'tokenData:', tokenData);
  const [whitelistEntries, setWhitelistEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    wallet_address: '',
    mint_address: tokenData?.mint_address || tokenData?.token_id || tokenId || '' // Try multiple sources for mint address
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [stats, setStats] = useState({
    unique_wallets: 0,
    unique_mints: 0,
    total_entries: 0
  });
  const [copiedId, setCopiedId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    entry: null
  });

  // Fetch whitelist entries
  const fetchWhitelist = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = tokenId ? `/api/whitelist?token_id=${encodeURIComponent(tokenId)}` : '/api/whitelist';
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setWhitelistEntries(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching whitelist:', error);
      setMessage({ type: 'error', text: 'Failed to fetch whitelist entries' });
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = tokenId ? `/api/whitelist/stats?token_id=${encodeURIComponent(tokenId)}` : '/api/whitelist/stats';
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchWhitelist();
    fetchStats();
  }, [tokenId]); // Refetch when tokenId changes

  // Update form data when tokenData becomes available
  useEffect(() => {
    const newMintAddress = tokenData?.mint_address || tokenData?.token_id || tokenId || '';
    if (newMintAddress && newMintAddress !== formData.mint_address) {
      setFormData(prev => ({
        ...prev,
        mint_address: newMintAddress
      }));
    }
  }, [tokenData?.mint_address, tokenData?.token_id, tokenId, formData.mint_address]);

  // Handle form input changes
  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      const dataToSubmit = { 
        ...formData,
        token_id: tokenId // Ensure token_id is included
      };
      const response = await axios.post('/api/whitelist', dataToSubmit, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Entry added to whitelist successfully!' });
        setFormData({ wallet_address: '', mint_address: tokenData?.mint_address || tokenData?.token_id || tokenId || '' }); // Reset but keep token
        fetchWhitelist();
        fetchStats();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to add entry to whitelist';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  // Open delete modal
  const openDeleteModal = (entry) => {
    setDeleteModal({
      isOpen: true,
      entry: entry
    });
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      entry: null
    });
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteModal.entry) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/whitelist/${deleteModal.entry.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage({ type: 'success', text: 'Entry removed successfully!' });
      closeDeleteModal();
      fetchWhitelist();
      fetchStats();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to remove entry' });
      closeDeleteModal();
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Format address for display
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  return (
    <div>
      {/* Description */}
      <div className="mb-6">
        <p className="text-gray-600 dark:text-gray-400">
          {tokenId ? 
            `Manage whitelisted wallet addresses for ${tokenData?.symbol || tokenData?.token_symbol || 'this token'}. Add wallet addresses that are authorized for trading.` :
            'Manage your whitelisted wallet and token addresses for automated trading. Add wallet and token pairs to control which tokens can be traded.'
          }
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Entries</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-1">
                {stats.total_entries}
              </p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Unique Wallets</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-1">
                {stats.unique_wallets}
              </p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
              <Wallet className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Unique Tokens</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mt-1">
                {stats.unique_mints}
              </p>
            </div>
            <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
              <Coins className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Add Entry Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {tokenId ? `Add Wallet to ${tokenData?.symbol || tokenData?.token_symbol || 'Token'} Whitelist` : 'Add New Whitelist Entry'}
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="wallet_address" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Wallet Address
              </label>
              <input
                type="text"
                id="wallet_address"
                name="wallet_address"
                value={formData.wallet_address}
                onChange={handleInputChange}
                placeholder="Enter Solana wallet address"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
                required
              />
            </div>

            <div>
              <label htmlFor="mint_address" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Token Mint Address
              </label>
              <input
                type="text"
                id="mint_address"
                name="mint_address"
                value={formData.mint_address}
                onChange={handleInputChange}
                placeholder={(tokenData?.mint_address || tokenData?.token_id) ? `Current token: ${(tokenData.mint_address || tokenData.token_id).slice(0, 8)}...${(tokenData.mint_address || tokenData.token_id).slice(-8)}` : (tokenId ? `Current token: ${tokenId}` : "Enter token mint address")}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
                disabled={!!(tokenData?.mint_address || tokenData?.token_id || tokenId)}
                required
              />
              {(tokenData?.mint_address || tokenData?.token_id || tokenId) && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {(tokenData?.mint_address || tokenData?.token_id) ? 'Using mint address for current token scope' : 'Token mint address is locked to the current token scope'}
                </p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm font-medium"
          >
            <Plus className="w-4 h-4 mr-2" />
            {submitting ? 'Adding...' : 'Add to Whitelist'}
          </button>
        </form>

        {/* Message Display */}
        {message.text && (
          <div className={`mt-4 p-4 rounded-md flex items-center space-x-2 ${
            message.type === 'success' 
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300'
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="text-sm">{message.text}</span>
          </div>
        )}
      </div>

      {/* Whitelist Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden transition-colors">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Whitelist Entries
          </h2>
        </div>

        {loading ? (
          <div className="p-6 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : whitelistEntries.length === 0 ? (
          <div className="p-6 text-center">
            <Shield className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">
              No whitelist entries yet. Add your first entry above!
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Wallet Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Token Mint Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Added On
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {whitelistEntries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-mono text-gray-900 dark:text-gray-100">
                          {formatAddress(entry.wallet_address)}
                        </span>
                        <button
                          onClick={() => copyToClipboard(entry.wallet_address, `wallet-${entry.id}`)}
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title="Copy full address"
                        >
                          {copiedId === `wallet-${entry.id}` ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-mono text-gray-900 dark:text-gray-100">
                          {formatAddress(entry.mint_address)}
                        </span>
                        <button
                          onClick={() => copyToClipboard(entry.mint_address, `mint-${entry.id}`)}
                          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          title="Copy full address"
                        >
                          {copiedId === `mint-${entry.id}` ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(entry.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => openDeleteModal(entry)}
                        className="inline-flex items-center px-3 py-1 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={closeDeleteModal}
          />
          
          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              {/* Modal Header */}
              <div className="bg-white dark:bg-gray-800 px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20 sm:mx-0 sm:h-10 sm:w-10">
                    <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-gray-100">
                      Remove Whitelist Entry
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to remove this whitelist entry? This action cannot be undone.
                      </p>
                      {deleteModal.entry && (
                        <div className="mt-4 space-y-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Wallet:</span>
                            <span className="font-mono text-gray-900 dark:text-gray-100">
                              {formatAddress(deleteModal.entry.wallet_address)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Token:</span>
                            <span className="font-mono text-gray-900 dark:text-gray-100">
                              {formatAddress(deleteModal.entry.mint_address)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Modal Footer */}
              <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="button"
                  className="inline-flex w-full justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors sm:ml-3 sm:w-auto"
                  onClick={handleDelete}
                >
                  Remove Entry
                </button>
                <button
                  type="button"
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-600 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors sm:mt-0 sm:w-auto"
                  onClick={closeDeleteModal}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Whitelist;